/**
 * RatingCard
 *
 * A component for collecting user ratings in chat messages
 *
 * Primary responsibilities:
 * - Display a title and description
 * - Allow rating on different scales (5-star, 10-point, emoji)
 * - Collect optional feedback
 * - Show confirmation after submission
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  IconStar,
  IconStarFilled,
  IconMoodSmile,
  IconMoodHappy,
  IconMoodSad,
  IconMoodConfuzed,
  IconMoodCry,
  IconChartBar,
  IconCheck
} from '@tabler/icons-react'

type RatingScale = 'stars' | 'numbers' | 'emoji'

interface RatingCardProps {
  title: string
  description?: string
  scale?: RatingScale
  maxRating?: number
  onSubmit?: (rating: number, feedback: string) => void
  className?: string
}

export const RatingCard: React.FC<RatingCardProps> = ({
  title,
  description,
  scale = 'stars',
  maxRating = scale === 'numbers' ? 10 : 5,
  onSubmit,
  className
}) => {
  const [rating, setRating] = useState<number | null>(null)
  const [hoveredRating, setHoveredRating] = useState<number | null>(null)
  const [feedback, setFeedback] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleRatingClick = (value: number) => {
    if (submitted) return
    setRating(value)
  }

  const handleMouseEnter = (value: number) => {
    if (submitted) return
    setHoveredRating(value)
  }

  const handleMouseLeave = () => {
    if (submitted) return
    setHoveredRating(null)
  }

  const handleSubmit = () => {
    if (rating === null || submitted) return

    if (onSubmit) {
      onSubmit(rating, feedback)
    }

    setSubmitted(true)
  }

  const renderRatingOption = (value: number) => {
    const isActive =
      hoveredRating !== null ? hoveredRating >= value : rating !== null && rating >= value

    if (scale === 'stars') {
      return isActive ? (
        <IconStarFilled
          size={24}
          className="cursor-pointer text-yellow-400 transition-transform hover:scale-110"
        />
      ) : (
        <IconStar
          size={24}
          className="cursor-pointer text-gray-300 transition-transform hover:scale-110 dark:text-gray-600"
        />
      )
    } else if (scale === 'numbers') {
      return (
        <div
          className={cn(
            'flex size-8 cursor-pointer items-center justify-center rounded-full transition-all',
            isActive
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
          )}
        >
          {value}
        </div>
      )
    } else if (scale === 'emoji') {
      // For 5-point emoji scale
      const emojiIcons = [
        <IconMoodCry key="1" size={24} className="text-red-500" />,
        <IconMoodSad key="2" size={24} className="text-orange-500" />,
        <IconMoodConfuzed key="3" size={24} className="text-yellow-500" />,
        <IconMoodSmile key="4" size={24} className="text-lime-500" />,
        <IconMoodHappy key="5" size={24} className="text-green-500" />
      ]

      const index = value - 1
      if (index >= 0 && index < emojiIcons.length) {
        return (
          <div
            className={cn(
              'cursor-pointer rounded-full p-1 transition-all',
              isActive ? 'scale-110 bg-gray-100 dark:bg-gray-800' : 'opacity-50 hover:opacity-80'
            )}
          >
            {emojiIcons[index]}
          </div>
        )
      }

      return null
    }

    return null
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <IconChartBar size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">Rate & Review</h3>
        </div>
      </div>

      <div className="p-4">
        <h4 className="mb-2 text-base font-medium">{title}</h4>
        {description && <p className="text-muted-foreground mb-4 text-sm">{description}</p>}

        {!submitted ? (
          <>
            <div
              className="my-4 flex items-center justify-center gap-2"
              onMouseLeave={handleMouseLeave}
            >
              {Array.from({ length: maxRating }, (_, i) => i + 1).map((value) => (
                <div
                  key={value}
                  onClick={() => handleRatingClick(value)}
                  onMouseEnter={() => handleMouseEnter(value)}
                >
                  {renderRatingOption(value)}
                </div>
              ))}
            </div>

            <Textarea
              placeholder="Share your feedback (optional)"
              className="my-4 resize-none"
              rows={3}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              disabled={submitted}
            />

            <div className="flex justify-end">
              <Button onClick={handleSubmit} disabled={rating === null || submitted}>
                Submit
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center py-6">
            <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
              <IconCheck size={24} className="text-green-600 dark:text-green-400" />
            </div>
            <h5 className="mb-1 text-lg font-medium">Thank you for your feedback!</h5>
            <p className="text-muted-foreground text-center text-sm">
              Your rating of {rating} out of {maxRating} has been recorded.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
